import { Component, OnInit, OnDestroy, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { PageBreadcrumbComponent } from '../../../shared/components/common/page-breadcrumb/page-breadcrumb.component';
import { ButtonComponent } from '../../../shared/components/ui/button/button.component';
import { ModalComponent } from '../../../shared/components/ui/modal/modal.component';
import { InvitationFormDrawerComponent } from '../invitation-form-drawer/invitation-form-drawer.component';
import { InvitationApiService } from '../services/invitation-api.service';
import { InvitationDrawerService } from '../services/invitation-drawer.service';
import {
  InvitationFilters,
  InvitationResponse,
  InvitationStatus,
  RoleResponse,
} from '../models/invitation.model';
import { DatetimeService } from '../../../shared/services/datetime.service';
import { ToastService } from '../../../shared/services/toast.service';
import { ConfirmDialogService } from '../../../shared/services/confirm-dialog.service';
import { PAGE_SIZE_OPTIONS_DEFAULT } from '../../../shared/utils/pagination.constants';

@Component({
  selector: 'app-invitation-list',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    PageBreadcrumbComponent,
    ButtonComponent,
    ModalComponent,
    InvitationFormDrawerComponent,
  ],
  templateUrl: './invitation-list.component.html',
})
export class InvitationListComponent implements OnInit, OnDestroy {
  // ── List state ──────────────────────────────────────────────────────────────
  invitations: InvitationResponse[] = [];
  loading = false;
  error = '';
  successMessage = '';

  // Paginación global
  globalPage = 0;
  globalTotalPages = 0;
  globalTotalElements = 0;

  readonly PAGE_SIZE_OPTIONS = PAGE_SIZE_OPTIONS_DEFAULT;
  private readonly PAGE_SIZE_KEY = 'invitation-list-page-size';
  pageSize: number = Number(localStorage.getItem(this.PAGE_SIZE_KEY)) || 20;

  // ── Row actions menu ──────────────────────────────────────────────────────────
  menuOpenId: number | null = null;
  menuPosition = { top: 0, left: 0 };

  // ── Ver detalle ────────────────────────────────────────────────────────────────
  detailInvitation: InvitationResponse | null = null;

  // ── Editar rol ───────────────────────────────────────────────────────────────
  editRoleInvitation: InvitationResponse | null = null;
  editRoleId: number | null = null;
  editRoleSaving = false;
  roles: RoleResponse[] = [];
  rolesLoading = false;

  // ── Reenviar ──────────────────────────────────────────────────────────────────
  resendingId: number | null = null;

  // ── Filters ──────────────────────────────────────────────────────────────────
  showFilters = false;

  emailFilter = '';
  roleFilter = undefined;
  statusFilter = undefined;
  fromDateFilter = '';
  toDateFilter = '';

  private readonly destroy$ = new Subject<void>();

  constructor(
    private invitationApi: InvitationApiService,
    private drawerService: InvitationDrawerService,
    private datetimeService: DatetimeService,
    private toast: ToastService,
    private confirmDialog: ConfirmDialogService,
  ) {}

  ngOnInit(): void {
    this.loadInvitations();
    this.loadRoles();

    this.drawerService.saved$
      .pipe(takeUntil(this.destroy$))
      .subscribe((inv) => {
        this.successMessage = `Invitación enviada a ${inv.mailInvited}`;
        this.globalPage = 0;
        this.globalTotalPages = 0;
        this.globalTotalElements = 0;
        this.loadInvitations();
        setTimeout(() => {
          this.successMessage = '';
        }, 5000);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ── Data loading ─────────────────────────────────────────────────────────────

  loadInvitations(): void {
    this.loading = true;
    this.error = '';

    const filters: InvitationFilters = {
      email: this.emailFilter,
      roleId: this.roleFilter,
      status: this.statusFilter,
      createdFrom: this.fromDateFilter,
      createdTo: this.toDateFilter,
    };

    this.invitationApi
      .listInvitations(this.globalPage, this.pageSize, filters)
      .subscribe({
        next: (res) => {
          this.invitations = res.content;
          this.globalPage = res.page ?? this.globalPage;
          this.globalTotalPages = res.totalPages;
          this.globalTotalElements = res.totalElements;
          this.loading = false;
        },
        error: (err) => {
          this.error = err.error?.message || 'Error al cargar las invitaciones';
          this.loading = false;
        },
      });
  }

  // ── Drawer ────────────────────────────────────────────────────────────────────

  openDrawer(): void {
    this.drawerService.open();
  }

  // ── Row actions menu ──────────────────────────────────────────────────────────

  toggleMenu(inv: InvitationResponse, event: MouseEvent): void {
    event.stopPropagation();
    if (this.menuOpenId === inv.id) {
      this.menuOpenId = null;
      return;
    }
    const btn = event.currentTarget as HTMLElement;
    const rect = btn.getBoundingClientRect();
    const MENU_WIDTH = 208;
    const menuHeight = this.estimateMenuHeight(inv);
    const spaceBelow = window.innerHeight - rect.bottom;
    const top =
      spaceBelow >= menuHeight + 4
        ? rect.bottom + 4
        : Math.max(8, rect.top - menuHeight - 4);
    this.menuPosition = {
      top,
      left: Math.max(8, rect.right - MENU_WIDTH),
    };
    this.menuOpenId = inv.id;
  }

  /** Alto aproximado del menú según las acciones visibles para este estado (inc11). */
  private estimateMenuHeight(inv: InvitationResponse): number {
    const ITEM_HEIGHT = 36;
    const DIVIDER_HEIGHT = 9;
    const CONTAINER_PADDING = 8;
    let itemCount = 1; // "Ver detalle" siempre visible
    if (this.canResend(inv)) itemCount++;
    if (this.canCopyLink(inv)) itemCount++;
    if (this.canEditRole(inv)) itemCount++;
    let height = itemCount * ITEM_HEIGHT + CONTAINER_PADDING;
    if (this.canRevoke(inv)) {
      height += DIVIDER_HEIGHT + ITEM_HEIGHT;
    }
    return height;
  }

  closeMenu(): void {
    this.menuOpenId = null;
  }

  get menuInvitation(): InvitationResponse | null {
    return this.menuOpenId === null
      ? null
      : (this.invitations.find((i) => i.id === this.menuOpenId) ?? null);
  }

  @HostListener('document:click')
  onDocumentClick(): void {
    this.menuOpenId = null;
  }

  @HostListener('window:scroll')
  onWindowScroll(): void {
    this.menuOpenId = null;
  }

  // ── Action availability by status ──────────────────────────────────────────────

  canResend(inv: InvitationResponse): boolean {
    return inv.status === 'PENDING' || inv.status === 'EXPIRED';
  }

  canEditRole(inv: InvitationResponse): boolean {
    return inv.status === 'PENDING' || inv.status === 'EXPIRED';
  }

  canCopyLink(inv: InvitationResponse): boolean {
    return (
      (inv.status === 'PENDING' || inv.status === 'EXPIRED') &&
      !!inv.invitationLink
    );
  }

  canDeleteInvitation(inv: InvitationResponse): boolean {
    return inv.status !== 'ACCEPTED' && inv.status !== 'PENDING';
  }

  canRevoke(inv: InvitationResponse): boolean {
    return inv.status === 'PENDING';
  }

  // ── Ver detalle ────────────────────────────────────────────────────────────────

  openDetail(inv: InvitationResponse): void {
    this.menuOpenId = null;
    this.detailInvitation = inv;
  }

  closeDetail(): void {
    this.detailInvitation = null;
  }

  // ── Reenviar ──────────────────────────────────────────────────────────────────

  resend(inv: InvitationResponse): void {
    this.menuOpenId = null;
    if (!this.canResend(inv)) return;

    this.resendingId = inv.id;
    this.invitationApi.resendInvitation(inv.id).subscribe({
      next: () => {
        this.resendingId = null;
        this.toast.success({
          title: 'Invitación reenviada',
          message: `Se reenvió la invitación a ${inv.mailInvited}.`,
        });
        this.loadInvitations();
      },
      error: (err) => {
        this.resendingId = null;
        this.toast.error({
          title: 'No se pudo reenviar',
          message: err.error?.message || 'No se pudo reenviar la invitación.',
        });
      },
    });
  }

  // ── Copiar enlace ───────────────────────────────────────────────────────────────

  copyLink(inv: InvitationResponse): void {
    this.menuOpenId = null;
    const link = inv.invitationLink;
    if (!link) {
      this.toast.error({
        title: 'Sin enlace',
        message: 'Esta invitación no tiene un enlace disponible.',
      });
      return;
    }
    navigator.clipboard.writeText(link).then(
      () =>
        this.toast.success({
          title: 'Enlace copiado',
          message: 'El enlace de invitación se copió al portapapeles.',
        }),
      () =>
        this.toast.error({
          title: 'Error',
          message: 'No se pudo copiar el enlace.',
        }),
    );
  }

  // ── Delete invitation ────────────────────────────────────────────────────────

  async deleteInvitation(inv: InvitationResponse): Promise<void> {
    if (!this.canDeleteInvitation(inv)) return;
    const ok = await this.confirmDialog.confirmDanger({
      title: 'Eliminar invitación',
      message: `¿Está seguro de eliminar la invitación para "${inv.mailInvited}"?`,
    });
    if (!ok) return;
    this.invitationApi.deleteInvitation(inv.id).subscribe({
      next: () => {
        this.toast.success({
          title: 'Invitación eliminada',
          message: 'Se eliminó la invitación correctamente.',
        });
        this.loadInvitations();
      },
      // Sin este manejador, un fallo del backend dejaba la pantalla MUDA: ni toast, ni
      // diálogo, y la fila seguía ahí. Se leía como "el módulo no me deja eliminar",
      // que es justo como se reportó. Revocar sí lo tenía; eliminar no.
      error: (err) => {
        this.toast.error({
          title: 'No se pudo eliminar',
          message:
            err.error?.message || 'No se pudo eliminar la invitación.',
        });
      },
    });
  }

  // ── Editar rol ───────────────────────────────────────────────────────────────

  openEditRole(inv: InvitationResponse): void {
    this.menuOpenId = null;
    if (!this.canEditRole(inv)) return;

    this.editRoleInvitation = inv;
    this.editRoleId = inv.roleId;
    this.loadRoles();
  }

  private loadRoles(): void {
    if (this.roles.length > 0) return;
    this.rolesLoading = true;
    this.invitationApi.listRoles().subscribe({
      next: (roles) => {
        this.roles = roles;
        this.rolesLoading = false;
      },
      error: () => {
        this.rolesLoading = false;
      },
    });
  }

  closeEditRole(): void {
    this.editRoleInvitation = null;
    this.editRoleId = null;
    this.editRoleSaving = false;
  }

  saveEditRole(): void {
    if (!this.editRoleInvitation || this.editRoleId === null) return;

    this.editRoleSaving = true;
    this.invitationApi
      .updateInvitationRole(this.editRoleInvitation.id, {
        roleId: this.editRoleId,
      })
      .subscribe({
        next: () => {
          this.editRoleSaving = false;
          this.toast.success({
            title: 'Invitación actualizada',
            message: 'Se actualizó el rol de la invitación correctamente.',
          });
          this.closeEditRole();
          this.loadInvitations();
        },
        error: (err) => {
          this.editRoleSaving = false;
          this.toast.error({
            title: 'No se pudo cambiar el rol',
            message:
              err.error?.message ||
              'No se pudo cambiar el rol de la invitación.',
          });
        },
      });
  }

  // ── Revocar ────────────────────────────────────────────────────────────────────

  async revoke(inv: InvitationResponse): Promise<void> {
    this.menuOpenId = null;
    if (!this.canRevoke(inv)) return;

    const ok = await this.confirmDialog.confirmDanger({
      title: 'Revocar invitación',
      message: `¿Seguro que deseas revocar la invitación de ${inv.mailInvited}? El enlace dejará de funcionar.`,
      confirmText: 'Revocar',
    });
    if (!ok) return;

    this.invitationApi.revokeInvitation(inv.id).subscribe({
      next: () => {
        this.toast.success({
          title: 'Invitación revocada',
          message: `Se revocó la invitación de ${inv.mailInvited}.`,
        });
        this.loadInvitations();
      },
      error: (err) => {
        this.toast.error({
          title: 'No se pudo revocar',
          message: err.error?.message || 'No se pudo revocar la invitación.',
        });
      },
    });
  }

  // ── Pagination ────────────────────────────────────────────────────────────────

  cambiarPagina(page: number): void {
    if (page < 0 || page >= this.globalTotalPages) return;
    this.globalPage = page;
    this.loadInvitations();
  }

  changePageSize(size: number): void {
    this.pageSize = size;
    localStorage.setItem(this.PAGE_SIZE_KEY, String(size));
    this.globalPage = 0;
    this.globalTotalPages = 0;
    this.globalTotalElements = 0;
    this.loadInvitations();
  }

  get pagesArray(): number[] {
    return Array.from({ length: this.globalTotalPages }, (_, i) => i + 1);
  }
  get hasPrevPage(): boolean {
    return this.globalPage > 0;
  }
  get hasNextPage(): boolean {
    return this.globalPage < this.globalTotalPages - 1;
  }
  get pageDisplay(): number {
    return this.globalPage + 1;
  }

  // ── Helpers ───────────────────────────────────────────────────────────────────

  statusLabel(status: InvitationStatus): string {
    const map: Record<InvitationStatus, string> = {
      PENDING: 'Pendiente',
      ACCEPTED: 'Aceptada',
      EXPIRED: 'Expirada',
      REVOKED: 'Revocada',
    };
    return map[status] ?? status;
  }

  statusColor(status: InvitationStatus): string {
    const map: Record<InvitationStatus, string> = {
      PENDING:
        'bg-warning-50 text-warning-600 dark:bg-warning-500/10 dark:text-warning-400',
      ACCEPTED:
        'bg-success-50 text-success-600 dark:bg-success-500/10 dark:text-success-400',
      EXPIRED: 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400',
      REVOKED:
        'bg-error-50 text-error-600 dark:bg-error-500/10 dark:text-error-400',
    };
    return map[status] ?? '';
  }

  formatDate(iso: string): string {
    if (!iso) return '—';
    return this.datetimeService.formatDateShort(iso);
  }

  timeUntil(iso: string): string {
    if (!iso) return '—';
    const diff = new Date(iso).getTime() - Date.now();
    if (diff <= 0) return 'Expirada';

    const mins = Math.floor(diff / 60000);
    const hours = Math.floor(mins / 60);
    const days = Math.floor(hours / 24);

    if (days > 0) {
      const remHours = hours % 24;
      return remHours > 0
        ? `Expira en ${days}d ${remHours}h`
        : `Expira en ${days} día${days !== 1 ? 's' : ''}`;
    }
    if (hours > 0) {
      const remMins = mins % 60;
      return remMins > 0
        ? `Expira en ${hours}h ${remMins}min`
        : `Expira en ${hours} hora${hours !== 1 ? 's' : ''}`;
    }
    return `Expira en ${mins} min`;
  }

  // ── Reset Filters ───────────────────────────────────────────────────────────────────

  resetFilters(): void {
    this.emailFilter = '';
    this.roleFilter = undefined;
    this.statusFilter = undefined;
    this.fromDateFilter = '';
    this.toDateFilter = '';
    this.loadInvitations();
  }

  // ── Set Filters ───────────────────────────────────────────────────────────────────

  searchInvitation() {
    this.globalPage = 0;
    this.globalTotalPages = 0;
    this.globalTotalElements = 0;
    this.loadInvitations();
  }
}
